/**
 * Cliente HTTP de la pestaña Asistencia al Viaje.
 *
 * Espejo de `back/src/asistencia/rutas/index.ts`. El front nunca habla con
 * GeoVictoria ni con los TMS: todo pasa por `/api/asistencia`.
 */

import {
  parchear,
  pedir,
  poner,
  postear,
  type OperacionHistorica,
  type JornadaDto,
  type ListadoJornadas,
  type ResultadoImpactoDto,
  type ResultadoMasivo,
} from '../../api';

const API = '/api/asistencia';

export interface FiltroJornadas {
  readonly fechaDesde: string;
  readonly fechaHasta: string;
  readonly dni?: string;
  readonly estado?: string;
  readonly grupo?: string;
  readonly empresa?: string;
}

/**
 * Consulta de jornadas.
 *
 * Recibe una señal porque es la consulta larga del portal —cruza GeoVictoria
 * con los TMS y tarda decenas de segundos—: la pantalla tiene que poder
 * cortarla si el usuario se arrepiente.
 */
export function listarJornadas(
  filtro: FiltroJornadas,
  signal?: AbortSignal,
): Promise<ListadoJornadas> {
  const p = new URLSearchParams({
    fechaDesde: filtro.fechaDesde,
    fechaHasta: filtro.fechaHasta,
    tamanio: '500',
  });
  if (filtro.dni?.trim()) p.set('dni', filtro.dni.trim());
  if (filtro.estado) p.set('estado', filtro.estado);
  if (filtro.grupo) p.set('grupo', filtro.grupo);
  if (filtro.empresa) p.set('empresa', filtro.empresa);

  return pedir<ListadoJornadas>(
    `${API}/jornadas?${p.toString()}`,
    signal ? { signal } : {},
  );
}

export interface Catalogos {
  readonly grupos: readonly string[];
}

/** Catálogo del filtro de grupos. Ya no hay unidades: se retiró el 14-09. */
export function catalogos(): Promise<Catalogos> {
  return pedir<Catalogos>(`${API}/grupos`);
}

const rutaJornada = (dni: string, fecha: string): string =>
  `${API}/jornadas/${encodeURIComponent(dni)}/${encodeURIComponent(fecha)}`;

export interface RespuestaProcesar {
  readonly resultados: readonly ResultadoImpactoDto[];
  /** La jornada ya reevaluada: la grilla actualiza esa fila y nada más. */
  readonly jornada: JornadaDto;
}

export function procesarJornada(dni: string, fecha: string): Promise<RespuestaProcesar> {
  return postear<RespuestaProcesar>(`${rutaJornada(dni, fecha)}/procesar`);
}

export interface ItemMasivo {
  readonly dni: string;
  readonly fecha: string;
}

export function impactarMasivo(items: readonly ItemMasivo[]): Promise<ResultadoMasivo> {
  return postear<ResultadoMasivo>(`${API}/impactos/masivo`, { items });
}

export function resolverJornada(
  dni: string,
  fecha: string,
  decision: string,
  comentario: string,
): Promise<JornadaDto> {
  return postear<JornadaDto>(`${rutaJornada(dni, fecha)}/resolver`, { decision, comentario });
}

export interface FiltroHistorico {
  readonly fechaDesde: string;
  readonly fechaHasta: string;
  readonly dni?: string;
  readonly clase?: string;
  readonly resultado?: string;
}

export function historico(
  filtro: FiltroHistorico,
): Promise<{ operaciones: readonly OperacionHistorica[] }> {
  const p = new URLSearchParams({
    fechaDesde: filtro.fechaDesde,
    fechaHasta: filtro.fechaHasta,
    limite: '300',
  });
  if (filtro.dni?.trim()) p.set('dni', filtro.dni.trim());
  if (filtro.clase) p.set('clase', filtro.clase);
  if (filtro.resultado) p.set('resultado', filtro.resultado);

  return pedir<{ operaciones: readonly OperacionHistorica[] }>(`${API}/historico?${p.toString()}`);
}

/** Ejecución automática del proceso. Espejo de `/api/asistencia/programacion`. */
/** Un grupo del universo configurado, con su operación y si está excluido. */
/** Un grupo que el proceso considera. */
export interface GrupoAlcanceDto {
  readonly grupo: string;
  /** Operación a la que se le consultan los viajes. Vacío mientras se elige. */
  readonly tms: string;
  /** Se le registra la licencia, pero no la salida al fin de turno. */
  readonly sinSalidaArtificial: boolean;
}

export interface AlcanceGruposDto {
  readonly grupos: readonly GrupoAlcanceDto[];
  /** Operaciones que el servidor sabe resolver, para el desplegable. */
  readonly operaciones: readonly string[];
}

export function leerGrupos(): Promise<AlcanceGruposDto> {
  return pedir<AlcanceGruposDto>(`${API}/grupos/alcance`);
}

/**
 * Los grupos de GeoVictoria que todavía no están en el proceso.
 *
 * Se pide al abrir el diálogo de agregar, no al cargar la pantalla: es una
 * llamada a un sistema externo y solo hace falta cuando se va a sumar uno.
 */
export function leerGruposDisponibles(): Promise<{ readonly grupos: readonly string[] }> {
  return pedir<{ grupos: readonly string[] }>(`${API}/grupos/disponibles`);
}

export function guardarGrupos(
  grupos: readonly GrupoAlcanceDto[],
): Promise<AlcanceGruposDto> {
  // El back reemplaza el universo completo: lo que no viaje queda fuera.
  return poner<AlcanceGruposDto>(`${API}/grupos/alcance`, {
    grupos: grupos.map(({ grupo, tms, sinSalidaArtificial }) => ({
      grupo,
      tms,
      sinSalidaArtificial,
    })),
  });
}

export interface ProgramacionDto {
  readonly activa: boolean;
  readonly horarios: readonly string[];
  readonly zona: string;
  readonly proximaCorrida: string | null;
  readonly ultimaCorridaProgramada: string | null;
  readonly ultimaCorridaEn: string | null;
  readonly ultimoResultado: string | null;
  readonly actualizadoPor: string;
  readonly actualizadoEn: string | null;
  readonly soloLectura: boolean;
  /** Esta instancia no dispara el proceso aunque la programación esté activa. */
  readonly automaticoApagado?: boolean;
}

export function leerProgramacion(): Promise<ProgramacionDto> {
  return pedir<ProgramacionDto>(`${API}/programacion`);
}

export function guardarProgramacion(cambio: {
  activa: boolean;
  horarios: readonly string[];
}): Promise<ProgramacionDto> {
  return poner<ProgramacionDto>(`${API}/programacion`, cambio);
}

// ------------------------------------------------------------- usuarios

import type { RolPortal, UsuarioDto } from '../../api';

export function listarUsuarios(): Promise<{ usuarios: readonly UsuarioDto[] }> {
  return pedir<{ usuarios: readonly UsuarioDto[] }>(`${API}/usuarios`);
}

export function crearUsuario(datos: {
  usuario: string;
  nombre: string;
  rol: RolPortal;
  clave: string;
}): Promise<UsuarioDto> {
  return postear<UsuarioDto>(`${API}/usuarios`, datos);
}

/**
 * Cambios sobre un usuario. Se manda solo lo que se tocó: mandar todo pisaría
 * con valores viejos lo que otro administrador cambió mientras tanto.
 */
export function modificarUsuario(
  usuario: string,
  cambio: { activo?: boolean; rol?: RolPortal; claveNueva?: string },
): Promise<UsuarioDto> {
  return parchear<UsuarioDto>(`${API}/usuarios/${encodeURIComponent(usuario)}`, cambio);
}

export function cambiarClavePropia(claveActual: string, claveNueva: string): Promise<void> {
  return postear<void>(`${API}/sesion/clave`, { claveActual, claveNueva });
}

// ---------------------------------------------------------- exportación

/**
 * Descarga la consulta en Excel.
 *
 * Va por `fetch` y no por un enlace directo para poder mostrar el error
 * dentro del portal: con un `<a href>`, un 401 o un 500 terminan pintando el
 * JSON crudo en una pestaña nueva.
 */
export async function descargarJornadasXlsx(
  filtro: FiltroJornadas & { soloPendientes?: boolean },
): Promise<{ blob: Blob; nombre: string }> {
  const p = new URLSearchParams({
    fechaDesde: filtro.fechaDesde,
    fechaHasta: filtro.fechaHasta,
  });
  if (filtro.dni?.trim()) p.set('dni', filtro.dni.trim());
  if (filtro.estado) p.set('estado', filtro.estado);
  if (filtro.grupo) p.set('grupo', filtro.grupo);
  if (filtro.empresa) p.set('empresa', filtro.empresa);
  if (filtro.soloPendientes) p.set('soloPendientes', 'true');

  const respuesta = await fetch(`${API}/jornadas.xlsx?${p.toString()}`, {
    credentials: 'same-origin',
  });

  if (!respuesta.ok) {
    // El error sí viene en JSON: se lee para mostrar el motivo de siempre.
    const texto = await respuesta.text();
    let motivo = `Error ${String(respuesta.status)}`;
    try {
      const cuerpo = JSON.parse(texto) as { error?: string };
      motivo = cuerpo.error ?? motivo;
    } catch {
      /* si no es JSON, queda el código de estado */
    }
    throw new Error(motivo);
  }

  // El nombre lo decide el back y viaja en la cabecera: así la planilla se
  // llama igual venga de donde venga.
  const cabecera = respuesta.headers.get('Content-Disposition') ?? '';
  const nombre = /filename="([^"]+)"/.exec(cabecera)?.[1] ?? 'asistencia-al-viaje.xlsx';

  return { blob: await respuesta.blob(), nombre };
}
